
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SubHeader from "../components/SubHeader";
import "../assets/css/Blog.css";

const SITE_URL = "https://www.erainterio.com";
const BLOG_URL = `${SITE_URL}/blog`;
const ARTICLE_SLUG = "wallpaper-interior-design-ideas";
const FEATURED_IMAGE = `${SITE_URL}/images/wallpaper-interior-1200x800.jpg`;
const ARTICLE_URL = `${BLOG_URL}/${ARTICLE_SLUG}`;

const blog = {
  category: "Wallpaper Design",
  title: "872+ Wallpaper Interior Design Ideas for Room Walls, Texture and 3D",
  description:
    "Explore wallpaper interior design ideas for living rooms, bedrooms and small spaces. Discover 3D, 5D, textured, floral and geometric wallpaper styles, along with practical selection tips.",
  image: "https://www.erainterio.com/static/media/about-img-1.46bf87808fc6f30b56af.jpg",
  alt: "Wallpaper interior design ideas for stylish room walls and modern homes",
  slug: ARTICLE_SLUG,
  featured_image : FEATURED_IMAGE
};

const Blog = () => {
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${BLOG_URL}#blog`,
    name: "Home Decor Ideas & Interior Design Inspiration | ERA Interio",
    url: BLOG_URL,
    description:
      "Explore home decor ideas, wallpaper designs, room inspiration and interior design tips from ERA Interio.",
    inLanguage: "en-IN",
    publisher: {
      "@type": "Organization",
      name: "ERA Interio",
      url: SITE_URL,
    },
    blogPost: {
      "@type": "BlogPosting",
      headline: blog.title,
      url: ARTICLE_URL,
      description: blog.description,
      image: `${SITE_URL}${blog.image}`,
      mainEntityOfPage: ARTICLE_URL,
      author: {
        "@type": "Organization",
        name: "ERA Interio",
      },
      publisher: {
        "@type": "Organization",
        name: "ERA Interio",
        url: SITE_URL,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>
          Home Decor Ideas & Interior Design Inspiration | ERA Interio
        </title>

        <meta
          name="description"
          content="Discover home decor ideas, wallpaper designs, living room inspiration and elegant interior decorating tips to transform your space with ERA Interio."
        />

        <meta
          name="keywords"
          content="Home Decor Ideas, Wallpaper Interior Design Ideas, decoration items for home, living room decor ideas, wall decor ideas, bedroom wallpaper, 3D wallpaper designs, interior design Nagpur"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large"
        />

        <link rel="canonical" href={BLOG_URL} />

        <meta
          property="og:title"
          content="Home Decor Ideas & Interior Design Inspiration | ERA Interio"
        />

        <meta
          property="og:description"
          content="Explore wallpaper designs, creative home decor ideas and beautiful interior inspiration for living rooms, bedrooms and modern homes."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={BLOG_URL} />

        <meta
          property="og:image"
          content={`${SITE_URL}${blog.image}`}
        />

        <meta
          property="og:image:alt"
          content={blog.alt}
        />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Home Decor Ideas & Interior Design Inspiration | ERA Interio"
        />

        <meta
          name="twitter:description"
          content={blog.description}
        />

        <meta
          name="twitter:image"
          content={`${SITE_URL}${blog.image}`}
        />

        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
      </Helmet>

      <SubHeader seoTitle="Home Decor Ideas & Interior Design Inspiration" />

      <main className="era-blog">
        <section className="blog-intro">
          <span className="blog-eyebrow">THE ERA JOURNAL</span>

          <h1>Home Decor Ideas for Stylish and Modern Spaces</h1>

          <p>
            Discover practical decorating inspiration, beautiful wall
            treatments and thoughtful interior design ideas. Explore
            wallpaper styles, room colours, textures and creative ways
            to make your home feel more personal.
          </p>
        </section>

        <section className="blog-featured container">
          <div className="row g-0 align-items-stretch">
            <div className="col-lg-7">
              <Link
                to={`/blog/${blog.slug}`}
                className="blog-featured-image"
                aria-label={`Read ${blog.title}`}
              >
                <img src={blog.featured_image} alt={blog.alt} />
                <span className="blog-image-label">
                  FEATURED STORY
                </span>
              </Link>
            </div>

            <div className="col-lg-5">
              <div className="blog-featured-content">
                <span className="blog-category">{blog.category}</span>

                <h2>{blog.title}</h2>

                <p>{blog.description}</p>

                <Link
                  to={`/blog/${blog.slug}`}
                  className="blog-read-link"
                >
                  Explore the Story <span>↗</span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="blog-listing container">
          <div className="blog-section-heading">
            <div>
              <span className="blog-eyebrow">EXPLORE OUR GUIDE</span>
              <h2>Wallpaper Ideas for Every Room</h2>
            </div>

            <p>
              Find inspiration for living rooms, bedrooms and other
              spaces with different wallpaper styles and textures.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-4">
              <article className="blog-card">
                <Link
                  to={`/blog/${blog.slug}`}
                  className="blog-card-image"
                  aria-label={`Read ${blog.title}`}
                >
                  <img
                    src={blog.featured_image}
                    alt={blog.alt}
                    loading="lazy"
                  />
                  <span className="blog-card-arrow">↗</span>
                </Link>

                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span>{blog.category}</span>
                    <span>Interior Design Guide</span>
                  </div>

                  <h3>
                    <Link to={`/blog/${blog.slug}`}>
                      {blog.title}
                    </Link>
                  </h3>

                  <p>{blog.description}</p>

                  <Link
                    to={`/blog/${blog.slug}`}
                    className="blog-read-link"
                  >
                    Read Article <span>↗</span>
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="blog-cta">
          <span className="blog-eyebrow">YOUR SPACE, YOUR STORY</span>

          <h2>Planning Your Dream Interior?</h2>

          <p>
            Let ERA Interio help you plan a beautiful space that
            reflects your style and preferences.
          </p>

          <Link to="/contact-us" className="blog-cta-button">
            Discuss Your Project <span>↗</span>
          </Link>
        </section>
      </main>
    </>
  );
};

export default Blog;
